import { ReactNode } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { Authentication } from '@components/integrations/integrations-types';
import { Menu } from '@components/menu';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { MarketplaceTemplate } from 'api/app-marketplace/requests';
import { useInvalidateJourneyActions } from 'api/journey-actions/hooks';
import { useInvalidateJourneyTravellerActions } from 'api/traveller-actions/hooks';
import { usePermissions } from 'contexts/permissions-context';
import {
  AuthLabel,
  sortAuthenticationsWithOAuthFirst,
} from 'ui/authenticated/settings/pages/integrations/app-marketplace/auth-utils';
import { useConnectModal } from 'ui/authenticated/settings/pages/integrations/app-marketplace/connect-modal';

export const AppNotConnectedSection = ({
  template,
  journeyActionId,
  travellerActionId,
}: {
  template?: MarketplaceTemplate | null;
  journeyActionId?: string;
  travellerActionId?: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const showConnectModal = useConnectModal();
  const { invalidateJourneyAction } = useInvalidateJourneyActions();
  const { invalidateJourneyTravellerAction } = useInvalidateJourneyTravellerActions();
  const { canManageIntegrations } = usePermissions();

  const handleConnectSuccess = () => {
    if (travellerActionId) {
      invalidateJourneyTravellerAction(travellerActionId);
    }
    if (journeyActionId) {
      invalidateJourneyAction(journeyActionId);
    }
  };

  const renderConnectButton = () => {
    if (!template) return null;

    const isEdit = template.connectionStatus !== 'not_connected';

    const openConnectModal = (auth: (typeof template.authentications)[0]) => {
      showConnectModal({
        template,
        authentication: auth,
        ...(journeyActionId && { journeyActionId }),
        isEdit,
        onSuccess: handleConnectSuccess,
      });
    };

    if (!canManageIntegrations) {
      return (
        <Button
          variant="primary-blue"
          text={t('connect')}
          disabled
          tooltip={t('noPermissionConnect')}
        />
      );
    }

    if (template.authentications.length > 1) {
      return (
        <Menu
          buttonProps={{
            variant: 'primary-blue',
            text: t('connect'),
          }}
        >
          {({ close }) =>
            sortAuthenticationsWithOAuthFirst(template.authentications).map((auth) => (
              <Menu.Item
                key={auth.shortId}
                label={
                  <AuthLabel
                    authType={auth.authType as Authentication['authType']}
                    authentications={template.authentications}
                  />
                }
                onSelect={() => {
                  openConnectModal(auth);
                  close();
                }}
              />
            ))
          }
        </Menu>
      );
    }

    return (
      <Button
        variant="primary-blue"
        text={t('connect')}
        onClick={() => openConnectModal(template.authentications[0])}
      />
    );
  };

  return (
    <Card variant="orange" spaceX={3} spaceY={4} hasBorder={false}>
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack space={4} orientation="horizontal">
          <IconFormIndicatorWarning />
          <Text text={t('appNotConnected')} color="textSecondary" font="regular14" />
        </Stack>
        {renderConnectButton()}
      </Stack>
    </Card>
  );
};

export const SidebarWarningSection = ({
  children,
  noMargin,
}: {
  children: ReactNode;
  noMargin?: boolean;
}) => (
  <Stack
    space={6}
    orientation="vertical"
    style={noMargin ? undefined : { marginBottom: pxToRem(24) }}
  >
    {children}
    <Sidebar.Separator />
  </Stack>
);

export const DisabledSection = styled.div({
  opacity: 0.5,
  pointerEvents: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space[6],
});

const locale = {
  en: {
    appNotConnected: 'App has not been connected',
    connect: 'Connect',
    noPermissionConnect: 'Only Admins & Editors can connect integrations',
  },
};
